import { z } from "zod";
import { apiBigIntIdSchema, apiIntIdSchema } from "./ids.js";
import { createPaginatedResponseSchema } from "./pagination.js";
export const socialCommentPlatformFilterSchema = z.enum(["INSTAGRAM", "FACEBOOK"]);
export const socialCommentHandlingStatusFilterSchema = z.enum(["OPEN", "HANDLED", "ALL"]);
export const socialCommentHandlingStatusSchema = z.enum(["OPEN", "HANDLED"]);
export const socialCommentAuthorTypeSchema = z.enum(["CUSTOMER", "BUSINESS_SELF", "UNKNOWN"]);
export const socialCommentLifecycleStateSchema = z.enum([
    "ACTIVE",
    "HIDDEN",
    "DELETED_ON_PROVIDER",
]);
export const socialPostMediaTypeSchema = z.enum([
    "IMAGE",
    "VIDEO",
    "CAROUSEL",
    "REEL",
    "TEXT",
    "LINK",
    "UNKNOWN",
]);
export const privateReplyEligibilityReasonSchema = z.enum([
    "ELIGIBLE",
    "ALREADY_SENT",
    "WINDOW_EXPIRED_7D",
    "SELF_COMMENT",
    "COMMENT_DELETED",
    "PERMISSION_MISSING",
    "ENDPOINT_DISCONNECTED",
    "PROVIDER_DISALLOWED",
]);
export const commentsSummaryResponseSchema = z.object({
    openCommentCount: z.number().int().nonnegative(),
    unreadOpenCommentCount: z.number().int().nonnegative(),
    byPlatform: z.object({
        INSTAGRAM: z.number().int().nonnegative(),
        FACEBOOK: z.number().int().nonnegative(),
    }),
    permissions: z.object({
        instagramNeedsReconnect: z.boolean(),
        facebookNeedsReconnect: z.boolean(),
    }),
});
export const socialCommentListQuerySchema = z.object({
    platform: socialCommentPlatformFilterSchema.optional(),
    platformEndpointId: z.coerce.number().int().positive().optional(),
    handlingStatus: socialCommentHandlingStatusFilterSchema.default("OPEN"),
    postId: apiBigIntIdSchema.optional(),
    search: z.string().trim().max(200).optional(),
    sort: z.enum(["activity_desc", "activity_asc"]).default("activity_desc"),
    cursor: z.string().min(1).optional(),
    limit: z.coerce.number().int().positive().max(50).default(30),
});
export const socialPostListQuerySchema = z.object({
    platform: socialCommentPlatformFilterSchema.optional(),
    platformEndpointId: z.coerce.number().int().positive().optional(),
    hasOpenComments: z
        .enum(["true", "false"])
        .optional()
        .transform((value) => (value === undefined ? undefined : value === "true")),
    search: z.string().trim().max(200).optional(),
    cursor: z.string().min(1).optional(),
    limit: z.coerce.number().int().positive().max(50).default(30),
});
export const socialCommentThreadQuerySchema = z.object({
    scope: z.enum(["thread", "all_post_comments"]).default("thread"),
    cursor: z.string().min(1).optional(),
    limit: z.coerce.number().int().positive().max(50).default(50),
});
export const updateSocialCommentHandlingStatusRequestSchema = z.object({
    handlingStatus: socialCommentHandlingStatusSchema,
});
export const socialCommentsSyncRequestSchema = z.object({
    platform: socialCommentPlatformFilterSchema.optional(),
});
export const socialCommentPublicReplyRequestSchema = z.object({
    content: z.string().trim().min(1).max(2200),
    clientIdempotencyKey: z.string().uuid(),
    /**
     * When true (default), mark the target customer comment HANDLED after provider-confirmed success.
     * When false (Send Only), leave existing handling status unchanged — never reopen HANDLED.
     */
    markHandled: z.boolean().default(true),
});
export const socialCommentPrivateReplyRequestSchema = z.object({
    content: z.string().trim().min(1).max(1000),
    clientIdempotencyKey: z.string().uuid(),
    /** Same semantics as public reply `markHandled`. */
    markHandled: z.boolean().default(true),
});
/**
 * Compact originating-comment card for Private Reply DM messages / conversation details.
 * Only present when a verified SocialComment ↔ Message (or Conversation) FK exists.
 */
export const messageOriginatingSocialCommentDtoSchema = z.object({
    commentId: apiBigIntIdSchema,
    platformType: socialCommentPlatformFilterSchema,
    content: z.string(),
    platformCreatedAt: z.string().datetime(),
    permalinkUrl: z.string().nullable(),
    authorDisplayName: z.string().nullable(),
    authorUsername: z.string().nullable(),
    postCaptionPreview: z.string().nullable(),
    postMediaPreviewUrl: z.string().nullable(),
    postPermalinkUrl: z.string().nullable(),
});
export const socialCommentListItemSchema = z.object({
    id: apiBigIntIdSchema,
    socialPostId: apiBigIntIdSchema,
    platformEndpointId: apiIntIdSchema,
    platformType: socialCommentPlatformFilterSchema,
    endpointLocalName: z.string().nullable(),
    accountUsername: z.string().nullable(),
    /** Connected Page / IG profile display name when Meta provides one (not the logged-in agent). */
    accountDisplayName: z.string().nullable(),
    externalCommentId: z.string().min(1),
    parentExternalCommentId: z.string().nullable(),
    authorType: socialCommentAuthorTypeSchema,
    authorUsername: z.string().nullable(),
    authorDisplayName: z.string().nullable(),
    content: z.string(),
    lifecycleState: socialCommentLifecycleStateSchema,
    handlingStatus: socialCommentHandlingStatusSchema,
    isRead: z.boolean(),
    publicReplyCount: z.number().int().nonnegative(),
    privateReplySentAt: z.string().datetime().nullable(),
    lastActivityAt: z.string().datetime(),
    platformCreatedAt: z.string().datetime(),
    postCaptionPreview: z.string().nullable(),
    postMediaType: socialPostMediaTypeSchema,
    postMediaPreviewUrl: z.string().nullable(),
});
export const socialCommentListResponseSchema = createPaginatedResponseSchema(socialCommentListItemSchema);
export const socialPostListItemSchema = z.object({
    id: apiBigIntIdSchema,
    platformEndpointId: apiIntIdSchema,
    platformType: socialCommentPlatformFilterSchema,
    endpointLocalName: z.string().nullable(),
    accountUsername: z.string().nullable(),
    accountDisplayName: z.string().nullable(),
    externalPostId: z.string().min(1),
    caption: z.string().nullable(),
    mediaType: socialPostMediaTypeSchema,
    mediaProductType: z.string().nullable(),
    mediaPreviewUrl: z.string().nullable(),
    permalinkUrl: z.string().nullable(),
    publishedAt: z.string().datetime().nullable(),
    totalCommentsCount: z.number().int().nonnegative(),
    openCommentsCount: z.number().int().nonnegative(),
    lastCommentAt: z.string().datetime().nullable(),
});
export const socialPostListResponseSchema = createPaginatedResponseSchema(socialPostListItemSchema);
export const socialPostDetailSchema = socialPostListItemSchema.extend({
    isDeletedOnProvider: z.boolean(),
});
export const socialCommentDetailSchema = socialCommentListItemSchema.extend({
    parentCommentId: apiBigIntIdSchema.nullable(),
    rootCommentId: apiBigIntIdSchema.nullable(),
    handledAt: z.string().datetime().nullable(),
    handledByMemberId: apiIntIdSchema.nullable(),
    handledReason: z.enum(["MANUAL", "PUBLIC_REPLY", "PRIVATE_REPLY"]).nullable(),
    readAt: z.string().datetime().nullable(),
    privateReplyConversationId: apiIntIdSchema.nullable(),
    privateReplyMessageId: apiBigIntIdSchema.nullable(),
    permalinkUrl: z.string().nullable(),
});
export const privateReplyEligibilitySchema = z.object({
    allowed: z.boolean(),
    reasonCode: privateReplyEligibilityReasonSchema,
    expiresAt: z.string().datetime().nullable(),
});
export const socialCommentThreadResponseSchema = z.object({
    post: socialPostDetailSchema,
    selectedComment: socialCommentDetailSchema,
    parentComment: socialCommentDetailSchema.nullable(),
    threadReplies: z.array(socialCommentDetailSchema),
    totalPostCommentsCount: z.number().int().nonnegative(),
    nextCursor: z.string().nullable(),
    privateReplyEligibility: privateReplyEligibilitySchema,
    existingConversation: z
        .object({
        conversationId: apiIntIdSchema,
        hasOpenMessagingWindow: z.boolean(),
    })
        .nullable(),
});
export const socialCommentsSyncResponseSchema = z.object({
    triggered: z.boolean(),
    skippedReason: z.enum(["COOLDOWN", "NO_ENDPOINTS", "PERMISSION_MISSING"]).nullable(),
    endpointResults: z.array(z.object({
        platformEndpointId: apiIntIdSchema,
        platformType: socialCommentPlatformFilterSchema,
        postsUpserted: z.number().int().nonnegative(),
        commentsUpserted: z.number().int().nonnegative(),
        errorCode: z.string().nullable(),
    })),
});
export const socialCommentReplyActionStatusSchema = z.enum([
    "PENDING",
    "SENDING",
    "SENT",
    "FAILED",
    "UNKNOWN_OUTCOME",
]);
export const socialCommentReplyActionSchema = z.object({
    id: apiBigIntIdSchema,
    socialCommentId: apiBigIntIdSchema,
    actionType: z.enum(["PUBLIC_REPLY", "PRIVATE_REPLY"]),
    status: socialCommentReplyActionStatusSchema,
    content: z.string(),
    clientIdempotencyKey: z.string().uuid(),
    externalResultId: z.string().nullable(),
    errorCode: z.string().nullable(),
    sentAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
});
export const socialCommentPublicReplyResponseSchema = z.object({
    replyAction: socialCommentReplyActionSchema,
    createdComment: socialCommentDetailSchema.nullable(),
    updatedComment: socialCommentDetailSchema,
});
export const socialCommentPrivateReplyResponseSchema = z.object({
    replyAction: socialCommentReplyActionSchema,
    conversationId: apiIntIdSchema.nullable(),
    messageId: apiBigIntIdSchema.nullable(),
    updatedComment: socialCommentDetailSchema,
});
//# sourceMappingURL=social-comments-contracts.js.map